/**
 * 虎鸡虫棒棋AI逻辑
 * 简化但有效的AI算法
 */

import { TigerChickenGameState, TigerChickenMove, Position, TigerChickenPiece, TigerChickenPieceType, Player, Difficulty, TIGER_CHICKEN_PIECE_VALUES } from '../types/game';
import { getAllPossibleMoves, checkGameEnd, canCapture } from './tigerChicken';

// 获取棋子价值
const getPieceValue = (piece: TigerChickenPiece): number => {
  return TIGER_CHICKEN_PIECE_VALUES[piece.type];
};

// 计算移动到目标位置的距离价值
const getDistanceValue = (from: Position, to: Position, targetHome: Position): number => {
  const currentDistance = Math.abs(from.row - targetHome.row) + Math.abs(from.col - targetHome.col);
  const newDistance = Math.abs(to.row - targetHome.row) + Math.abs(to.col - targetHome.col);
  return (currentDistance - newDistance) * 5; // 越接近目标大本营越好
};

// 评估移动的价值
const evaluateMove = (gameState: TigerChickenGameState, move: TigerChickenMove): number => {
  let score = 0;

  // 获胜移动最高优先级
  if (move.isWinningMove) {
    return 10000;
  }

  // 吃子移动有较高价值
  if (move.isCapture && move.captures) {
    score += getPieceValue(move.captures[0]) * 10;
  }

  // 计算靠近敌方大本营的价值
  const enemyHome = gameState.currentPlayer === 'black' ? gameState.whiteHome : gameState.blackHome;
  score += getDistanceValue(move.from, move.to, enemyHome);

  // 计算保护自己大本营的价值
  const myHome = gameState.currentPlayer === 'black' ? gameState.blackHome : gameState.whiteHome;
  const currentHomeDistance = Math.abs(move.from.row - myHome.row) + Math.abs(move.from.col - myHome.col);
  const newHomeDistance = Math.abs(move.to.row - myHome.row) + Math.abs(move.to.col - myHome.col);

  // 如果移动后离自己大本营更远，稍微减分（除非是去攻击）
  if (newHomeDistance > currentHomeDistance && !move.isCapture) {
    score -= 3;
  }

  // 中心位置有额外价值
  if (move.to.row >= 2 && move.to.row <= 4 && move.to.col >= 2 && move.to.col <= 4) {
    score += 2;
  }

  // 在对角线上移动有额外价值（因为可以斜向攻击）
  const isOnMainDiagonal = move.to.row === move.to.col;
  const isOnAntiDiagonal = move.to.row + move.to.col === 6;
  if (isOnMainDiagonal || isOnAntiDiagonal) {
    score += 3;
  }

  return score;
};

// 简单AI决策函数
export const getTigerChickenAIDecision = (
  gameState: TigerChickenGameState,
  difficulty: Difficulty = 'medium'
): TigerChickenMove | null => {
  try {
    const possibleMoves = getAllPossibleMoves(gameState, gameState.currentPlayer);

    if (possibleMoves.length === 0) {
      return null;
    }

    // 根据难度选择策略
    switch (difficulty) {
      case 'easy':
        return getEasyMove(possibleMoves);

      case 'medium':
        return getMediumMove(gameState, possibleMoves);

      case 'hard':
        return getHardMove(gameState, possibleMoves);

      default:
        return getMediumMove(gameState, possibleMoves);
    }
  } catch (error) {
    console.error('AI决策错误:', error);
    return null;
  }
};

// 简单模式：主要随机，优先吃子和获胜
const getEasyMove = (possibleMoves: TigerChickenMove[]): TigerChickenMove => {
  // 优先选择获胜移动
  const winningMoves = possibleMoves.filter(move => move.isWinningMove);
  if (winningMoves.length > 0) {
    return winningMoves[Math.floor(Math.random() * winningMoves.length)];
  }

  // 然后选择吃子移动
  const captureMoves = possibleMoves.filter(move => move.isCapture);
  if (captureMoves.length > 0) {
    return captureMoves[Math.floor(Math.random() * captureMoves.length)];
  }

  // 否则随机选择
  return possibleMoves[Math.floor(Math.random() * possibleMoves.length)];
};

// 中等模式：基于简单评估
const getMediumMove = (gameState: TigerChickenGameState, possibleMoves: TigerChickenMove[]): TigerChickenMove => {
  // 优先选择获胜移动
  const winningMoves = possibleMoves.filter(move => move.isWinningMove);
  if (winningMoves.length > 0) {
    return winningMoves[0];
  }

  // 评估所有移动
  const evaluatedMoves = possibleMoves.map(move => ({
    move,
    score: evaluateMove(gameState, move)
  }));

  // 按分数排序
  evaluatedMoves.sort((a, b) => b.score - a.score);

  // 在前几个最佳移动中随机选择（增加一些变化性）
  const topMoves = evaluatedMoves.slice(0, Math.min(3, evaluatedMoves.length));
  return topMoves[Math.floor(Math.random() * topMoves.length)].move;
};

// 困难模式：更深入的评估
const getHardMove = (gameState: TigerChickenGameState, possibleMoves: TigerChickenMove[]): TigerChickenMove => {
  // 优先选择获胜移动
  const winningMoves = possibleMoves.filter(move => move.isWinningMove);
  if (winningMoves.length > 0) {
    return winningMoves[0];
  }

  // 评估所有移动
  const evaluatedMoves = possibleMoves.map(move => ({
    move,
    score: evaluateMove(gameState, move)
  }));

  // 按分数排序，选择最佳移动
  evaluatedMoves.sort((a, b) => b.score - a.score);
  return evaluatedMoves[0].move;
};

export default {
  getTigerChickenAIDecision
};